import type { CartItem, GiftcardCartItem } from '~/composables/types/cart'
import { useEmbeddedCheckout } from '~/composables/useEmbeddedCheckout'
import { useIsSubdomain, useSubdomainData } from '~/composables/useSubdomain'
import { getAllAddonsForOrder } from '~/utils/cartGetters'
import {
  buildEventCheckoutLine,
  buildGiftCardCheckoutLine,
  resolveBookingGiftcardSupplierContext,
} from '~/utils/checkoutLinePayload'

export const useCartValidation = () => {
  const { cart } = useCartWithSubdomainLogic()
  const interval = ref()
  const { requireAgeOfMajority, handleValidationErrors } =
    useCartErrorHandlers()
  const { initializePricings } = useInitializePricingsForLegacyCart(cart)
  const isSubdomain = useIsSubdomain()
  const { isEmbedded, supplierIdParam } = useEmbeddedCheckout()
  const { data: subdomainSupplierData } = useSubdomainData()

  const performValidation = async () => {
    if (cart.value.items && cart.value.items.length === 0) return
    await initializePricings()

    const external = Boolean(isSubdomain.value || isEmbedded.value)
    const bookingSupplierFallback = external
      ? resolveBookingGiftcardSupplierContext({
          embeddedSupplierId: supplierIdParam.value as string | undefined,
          subdomainSupplierId: subdomainSupplierData.value?.id,
        })
      : undefined

    const lineItems = cart.value.items.map((item: CartItem) => {
      if (item.type === 'GIFT_CARD') {
        return buildGiftCardCheckoutLine(item as GiftcardCartItem, {
          external,
          bookingSupplierFallback,
        })
      }
      if (item.type === 'EVENT') {
        return buildEventCheckoutLine(item, { includePrice: true })
      }
      return {
        type: item.type,
        quantity: item.quantity,
      }
    })

    const items = [...lineItems, ...getAllAddonsForOrder(cart.value)]

    await kftApiRequest('/v1/checkout/cart/validate', {
      method: 'POST',
      body: {
        external,
        items,
      },
    }).catch(async (e) => {
      await handleValidationErrors(e)
    })
  }

  const setupValidationPolling = async () => {
    await performValidation()
    const oneMinute = 60000
    interval.value = setInterval(performValidation, oneMinute * 10)
  }

  const deactivateValidationPolling = async () => {
    await performValidation()
    clearInterval(interval.value)
  }

  return {
    requireAgeOfMajority,
    setupValidationPolling,
    performValidation,
    deactivateValidationPolling,
  }
}
